import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Typography } from '@mui/material';
import { useNotify } from 'react-admin';
import { IProductLine, setAdminProducts } from '../../../Services/productEntitlements';
import { ProductLinesTable } from './ProductLinesTable';
import { ContractUsageBlock } from './ContractUsageBlock';

interface Props {
	billingAccount: any;
	/** Les 6 lignes du compte (chargées par la fiche). */
	lines: IProductLine[];
	onChanged: () => void;
}

// Produits d'un compte SOUS CONTRAT (Entreprise, pricing v2). Les 6 lignes sont
// `admin` : la quantité est celle du contrat Pennylane, saisie ici, jamais
// écrasée par Stripe. Elle PLAFONNE la création, sauf si la case « Illimité »
// du produit est cochée : le compte peut alors dépasser. La consommation de
// l'année donne ce qu'il faut pour négocier l'ajustement.
//
// ⚠️ Le rapport de DÉPASSEMENTS mensuel qui occupait cette place a été retiré le
// 2026-09-28 : il appliquait l'inclus du forfait Pay as you go à un compte qui
// n'en a pas, sur un mois calendaire qui n'est pas sa période. Un compte sous
// contrat n'a ni inclus ni dépassement — il a une dépense.
//
// Un compte piloté par Stripe n'affiche PAS ce bloc : ses produits se lisent dans
// le bloc Stripe, en lecture seule, puisque c'est l'abonnement qui les possède.
export const BillingAccountProducts: React.FC<Props> = ({ billingAccount, lines, onChanged }) => {
	const notify = useNotify();
	const [draft, setDraft] = useState<Record<string, string>>({});
	const [unlimitedDraft, setUnlimitedDraft] = useState<Record<string, boolean>>({});
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		setDraft(Object.fromEntries(lines.map((x) => [x.product, String(x.quantity)])));
		setUnlimitedDraft(Object.fromEntries(lines.map((x) => [x.product, x.unlimited])));
	}, [lines]);

	const lineChanged = (l: IProductLine) =>
		(draft[l.product] !== undefined && Number(draft[l.product]) !== l.quantity) ||
		(unlimitedDraft[l.product] !== undefined && unlimitedDraft[l.product] !== l.unlimited);
	const dirty = lines.some(lineChanged);

	const save = async () => {
		setSaving(true);
		try {
			const changed = lines.filter(lineChanged).map((l) => ({
				product: l.product,
				quantity: Math.max(0, Number(draft[l.product]) || 0),
				options: l.options,
				unlimited: unlimitedDraft[l.product] ?? l.unlimited,
			}));
			await setAdminProducts(billingAccount.id, changed);
			notify('Quantités du contrat enregistrées', { type: 'success' });
			onChanged();
		} catch (e: any) {
			notify(e?.message || 'Échec de l’enregistrement', { type: 'error' });
		} finally {
			setSaving(false);
		}
	};

	return (
		<Card>
			<CardContent>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
					<Box>
						<Typography variant="h6">Produits du contrat</Typography>
						<Typography variant="caption" color="text.secondary">
							Compte Entreprise sous contrat — la quantité convenue plafonne, sauf si « Illimité » est coché.
						</Typography>
					</Box>
					<Button variant="contained" size="small" disabled={!dirty || saving} onClick={save}>
						Enregistrer
					</Button>
				</Box>
				<Alert severity="info" sx={{ mb: 2 }}>
					Les quantités sont celles du <strong>contrat</strong> (Pennylane), jamais écrasées par Stripe. 0 = pas d’accès. Par
					défaut la quantité <strong>plafonne</strong> : le compte ne crée pas au-delà. Cocher <strong>Illimité</strong> lève le
					plafond : il peut dépasser, et l’écart se lit dans la consommation ci-dessous.
				</Alert>
				<ProductLinesTable
					lines={lines}
					editable
					draft={draft}
					onDraftChange={(p, v) => setDraft((d) => ({ ...d, [p]: v }))}
					unlimitedDraft={unlimitedDraft}
					onUnlimitedChange={(p, v) => setUnlimitedDraft((d) => ({ ...d, [p]: v }))}
				/>

				<ContractUsageBlock billingAccountId={billingAccount.id} />
			</CardContent>
		</Card>
	);
};
